import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropertyInputV2 from '../v2/components/PropertyInputV2';
import { EDIT_INPUT_MODE } from '../v2/types/PropertyInputV2Component';
import { buildPropertiesFormInitializationFailedState, buildPropertiesFormInitializingState, buildPropertiesFormInvalidState, buildPropertiesFormPendingState, buildPropertiesFormValidState, getDependentFormProperties, getFormPropertyNames, getFormPropertiesDifference } from './utils';
import usePropertiesFormApi from './usePropertiesFormApi';
import useConditionalPropertyRules from './useConditionalPropertyRules';
import { useConditionalPropertyOptionsRules } from './useConditionalPropertyOptionsRules';
import { getChangedPropertiesWithInvalidDependentOptions, getPropertiesWithInvalidStatus, getRequiredPropertiesWithNoValue } from './formStatusUtils';
import useConditionalPropertyDependencies from './useConditionalPropertyDependencies';
import { useStableRef } from '../utils/hooks/useStableRef';
import { useHighlySensitivePropertyValues } from '../dataSensitivity/useHighlySensitivePropertyValues';
import { createElement as _createElement } from "react";
const emptyInputs = [];
const initializingState = buildPropertiesFormInitializingState();
const failedInitializingState = buildPropertiesFormInitializationFailedState();
const deriveFormStatus = (getFormValue, properties, allPropertyValidationStates, inputs, activeRules, changedPropertyNames, alwaysShowConditionalPropertyOptionsWithErrors) => {
  const allValidationStates = [...allPropertyValidationStates.entries()];
  const isLoading = allValidationStates.some(([__, {
    loading
  }]) => loading);
  if (isLoading) {
    return buildPropertiesFormPendingState();
  }
  const errors = new Map();

  // Collect required properties that don't have a value
  const requiredPropertiesWithNoValue = getRequiredPropertiesWithNoValue(inputs, getFormValue);
  if (requiredPropertiesWithNoValue.length > 0) {
    errors.set('MISSING_REQUIRED_PROPERTY', requiredPropertiesWithNoValue);
  }
  const changedPropertiesWithInvalidDependentOptions = getChangedPropertiesWithInvalidDependentOptions(activeRules, changedPropertyNames, properties, alwaysShowConditionalPropertyOptionsWithErrors, getFormValue);
  const propertiesWithInvalidStatus = getPropertiesWithInvalidStatus(allValidationStates, properties);
  const invalidProperties = [...changedPropertiesWithInvalidDependentOptions, ...propertiesWithInvalidStatus];
  if (invalidProperties.length > 0) errors.set('INVALID_PROPERTIES', [...changedPropertiesWithInvalidDependentOptions, ...propertiesWithInvalidStatus]);

  // In case of property validations, there is an UNKNOWN state when the validation API call fails.
  // PropertyInput treats it as a valid scenario (saveable) as BE provides defense in depth if a bad value slips through.
  // PropertiesForm also treats those properties as saveable and does not explicitly list them with an UNKNOWN state
  return errors.size > 0 ? buildPropertiesFormInvalidState(errors) : buildPropertiesFormValidState();
};
const makeNewFormInput = ({
  properties,
  property,
  getFormValue,
  handleChange,
  handleValidationChange,
  objectId,
  objectTypeId,
  inputMode,
  showPropertyDescription
}) => {
  const propertyName = property.propertyName;
  const propertyDefinition = properties[propertyName];

  // protects the form in case of misspelled or unavailable properties
  if (!propertyDefinition) {
    return undefined;
  }
  const dependents = [];
  for (const dependentProperty of property.dependents) {
    const dependentInput = makeNewFormInput({
      properties,
      property: dependentProperty,
      getFormValue,
      handleChange,
      handleValidationChange,
      objectId,
      objectTypeId,
      inputMode,
      showPropertyDescription
    });
    if (dependentInput !== undefined) {
      dependents.push(dependentInput);
    }
  }

  // Setting value as undefined in an input will convert the input to uncontrolled state.
  // So, falling back to null.
  const value = getFormValue(propertyName) || null;
  return {
    property: propertyDefinition,
    isRequired: property.isRequired,
    dependents,
    render: additionalPropertyInputProps => {
      return /*#__PURE__*/_createElement(PropertyInputV2, Object.assign({}, additionalPropertyInputProps, {
        onChange: handleChange,
        onValidationChange: handleValidationChange,
        getAdditionalPropertyValue: getFormValue,
        enableConditionalPropertiesPanel: false,
        enableConditionalPropertyOptions: true,
        inputMode: inputMode,
        key: propertyDefinition.name,
        objectId: objectId,
        objectTypeId: objectTypeId,
        formControlProps: Object.assign({
          label: propertyDefinition.label
        }, additionalPropertyInputProps ? additionalPropertyInputProps.formControlProps : undefined, {
          required: property.isRequired,
          help: showPropertyDescription ? propertyDefinition.description : undefined
        }),
        property: propertyDefinition,
        value: value
      }));
    }
  };
};
const propertiesEqualityKey = properties => properties.map(({
  propertyName,
  isRequired
}) => `${propertyName}/${isRequired}`).join(':');
const defaultHighlySensitivePropertyValues = {};
const useNewPropertiesForm = ({
  fetchConditionalPropertyRules,
  objectId,
  objectTypeId,
  onChange,
  properties: propertiesUnstable,
  values,
  alwaysShowConditionalProperties = false,
  alwaysShowConditionalPropertyOptionsWithErrors = false,
  inputMode = EDIT_INPUT_MODE,
  showPropertyDescription = false,
  getSavedValue
}) => {
  const [validationStates, setValidationStates] = useState(new Map());
  const [changedProperties, setChangedProperties] = useState(new Set());
  const [initialValuesCache, setInitialValuesCache] = useState({});
  const onChangeRef = useRef();
  onChangeRef.current = onChange;
  const propertiesRef = useStableRef(propertiesUnstable, (prev, next) => propertiesEqualityKey(prev) === propertiesEqualityKey(next));
  const valuesRef = useRef();
  valuesRef.current = values;
  const formPropertiesRef = useRef([]);

  // fetches all data and dispatches action with updated data
  const {
    loading,
    error,
    properties,
    conditionalPropertyRules,
    conditionalPropertyOptionsRules,
    cachedPropertyValues
  } = usePropertiesFormApi({
    objectTypeId,
    objectId,
    fetchConditionalPropertyRules
  });
  const highlySensitivePropertyValues = useHighlySensitivePropertyValues({
    objectTypeId,
    objectId,
    defaultValues: defaultHighlySensitivePropertyValues
  });

  // returns the current value for the given property. this value is shown in
  // the property input, used to calculate active CP and CPO rules, and used for
  // required-property validation. the current value is found by checking the
  // following sources, in order:
  //
  // 1. host app draft values given in the `values` prop
  // 2. host app values returned by `getSavedValue`
  // 3. cached values fetched from the api via `usePropertiesFormApi`
  const getFormValue = useCallback(propertyName => {
    if (Object.prototype.hasOwnProperty.call(values, propertyName)) {
      return values[propertyName];
    }
    if (Object.prototype.hasOwnProperty.call(highlySensitivePropertyValues, propertyName)) {
      return highlySensitivePropertyValues[propertyName];
    }
    const additionalPropertyValue = getSavedValue(propertyName, objectId, objectTypeId);
    if (additionalPropertyValue !== undefined) {
      return additionalPropertyValue;
    }
    if (Object.prototype.hasOwnProperty.call(cachedPropertyValues, propertyName)) {
      return cachedPropertyValues[propertyName];
    }
    return undefined;
  }, [values, objectId, objectTypeId, getSavedValue, cachedPropertyValues, highlySensitivePropertyValues]);
  const conditionalPropertyDependencies = useConditionalPropertyDependencies({
    loading,
    error,
    objectTypeId,
    getFormValue,
    properties,
    conditionalPropertyRules
  });
  const formPropertiesWithConditionalPropertyRules = useConditionalPropertyRules({
    properties: propertiesRef.current,
    changedProperties,
    alwaysShowConditionalProperties,
    conditionalPropertyDependencies
  });
  const {
    activeRules,
    formProperties,
    resetConditionalPropertyOptionsRules
  } = useConditionalPropertyOptionsRules({
    conditionalPropertyOptionsRules,
    formProperties: formPropertiesWithConditionalPropertyRules,
    getFormValue,
    properties,
    changedProperties,
    dependencies: conditionalPropertyDependencies,
    alwaysShowConditionalPropertyOptionsWithErrors
  });

  // wrapper to update current validation state to derive form status
  const handleValidationChange = useCallback(validationState => {
    setValidationStates(currentValidationState => new Map(currentValidationState).set(validationState.propertyName, validationState));
  }, []);

  // wrapper to update current values and pass to host apps
  const handleChange = useCallback(changes => {
    const propertyNames = Object.keys(changes);
    setChangedProperties(current => new Set([...current, ...propertyNames]));
    if (onChangeRef.current) {
      onChangeRef.current(Object.assign({}, valuesRef.current, changes));
    }
  }, []);
  const inputs = useMemo(() => {
    if (loading || error) {
      return emptyInputs;
    }
    const newInputs = [];
    for (const property of formProperties) {
      const newFormInput = makeNewFormInput({
        properties,
        property,
        getFormValue,
        handleChange,
        handleValidationChange,
        objectId,
        objectTypeId,
        inputMode,
        showPropertyDescription
      });
      if (newFormInput !== undefined) {
        newInputs.push(newFormInput);
      }
    }
    return newInputs;
  }, [loading, error, formProperties, properties, getFormValue, handleChange, handleValidationChange, objectId, objectTypeId, inputMode, showPropertyDescription]);
  useEffect(() => {
    const prevFormProperties = formPropertiesRef.current;
    formPropertiesRef.current = formProperties;
    if (prevFormProperties === formProperties) {
      // nothing to do; only check for changes if the list of dependents changed
      return;
    }
    const prevDependents = getDependentFormProperties(prevFormProperties);
    const dependents = getDependentFormProperties(formProperties);
    const allPropertyNames = getFormPropertyNames(formProperties);
    const {
      added,
      removed
    } = getFormPropertiesDifference(prevDependents, dependents);

    // when a dependent property appears:
    // * check to see if host app already provides a value (proactively)
    //   for the newly-visible property
    // * if yes, store that value in `initialValuesCache` so that when
    //   the property disappears, we can revert to the original value
    // * if not, store nothing in `initialValuesCache`
    if (added.length) {
      setInitialValuesCache(prev => {
        const next = Object.assign({}, prev);
        for (const propertyName of added) {
          if (Object.prototype.hasOwnProperty.call(values, propertyName)) {
            next[propertyName] = values[propertyName];
          }
        }
        return next;
      });
    }

    // when a dependent property disappears:
    // * if the property is present elsewhere on the form, do nothing
    // * check to see if the host app provides a value for the hidden
    //   property
    // * if yes, revert its value to whatever value was provided by the host
    //   app at the time the property appeared (if any)
    // * if no, remove the value from the draft values entirely
    if (removed.length) {
      // properties which no longer appear anywhere in the form
      const allOccurrencesRemoved = removed.filter(propertyName => !allPropertyNames.has(propertyName));
      let triggerOnChange = false;
      const newValues = Object.assign({}, valuesRef.current);
      for (const propertyName of allOccurrencesRemoved) {
        if (!Object.prototype.hasOwnProperty.call(newValues, propertyName)) {
          // host app did not provide a value for this property and the user did not edit it - no action needed
          continue;
        }
        if (Object.prototype.hasOwnProperty.call(initialValuesCache, propertyName)) {
          // host app provided a value for this property when it appeared - revert to that value
          newValues[propertyName] = initialValuesCache[propertyName];
        } else {
          // host app did not provide a value for this property but the user edited it - we are safe to throw away the value
          delete newValues[propertyName];
        }
        triggerOnChange = true;
      }
      if (triggerOnChange) {
        if (onChangeRef.current) {
          onChangeRef.current(newValues);
        }
      }
      setValidationStates(prev => {
        const next = new Map(prev);
        // if property is still in the form as a root input - no action needed
        for (const propertyName of allOccurrencesRemoved) {
          next.delete(propertyName);
        }
        return next;
      });
      setChangedProperties(prev => {
        const next = new Set(prev);
        // if property is still in the form as a root input - no action needed
        for (const propertyName of allOccurrencesRemoved) {
          next.delete(propertyName);
        }
        return next;
      });
      setInitialValuesCache(prev => {
        const next = Object.assign({}, prev);
        for (const propertyName of removed) {
          delete next[propertyName];
        }
        return next;
      });
    }
  }, [formProperties, initialValuesCache, values]);
  const status = useMemo(() => deriveFormStatus(getFormValue, properties, validationStates, inputs, activeRules, changedProperties, alwaysShowConditionalPropertyOptionsWithErrors), [inputs, properties, validationStates, getFormValue, activeRules, changedProperties, alwaysShowConditionalPropertyOptionsWithErrors]);
  const reset = useCallback(() => {
    setValidationStates(new Map());
    setChangedProperties(new Set());
    setInitialValuesCache({});
    resetConditionalPropertyOptionsRules();
    formPropertiesRef.current = [];
  }, [resetConditionalPropertyOptionsRules]);
  if (error) {
    return {
      status: failedInitializingState,
      inputs: emptyInputs,
      reset
    };
  } else if (loading) {
    return {
      status: initializingState,
      inputs: emptyInputs,
      reset
    };
  } else {
    return {
      status,
      inputs,
      reset
    };
  }
};
export default useNewPropertiesForm;